<div class="pxl-sidebar">

  <!-- ── REALTIME COLLABORATION FEED ── -->
  <div class="pxl-section collab-feed-section" *ngIf="isInRoom">
    <div class="pxl-section-label">
      <span class="collab-status-dot" [style.background]="statusColor"></span>
      💬 Chat · {{ roomId }}
    </div>
    <div class="collab-peers-mini">
      <div class="peer-chip" *ngFor="let p of peers">
        <span class="peer-avatar-sm" [style.background]="p.color">{{ p.avatar }}</span>
        <span class="peer-name">{{ p.username }}</span>
        <span class="peer-tool">
          <ng-container [ngSwitch]="p.activeTool">
            <span *ngSwitchCase="'draw'">🖌</span>
            <span *ngSwitchCase="'erase'">🧹</span>
            <span *ngSwitchCase="'fill'">🪣</span>
            <span *ngSwitchDefault>✏</span>
          </ng-container>
        </span>
      </div>
      <div class="no-peers" *ngIf="peers.length === 0">You are the only one here</div>
    </div>
  </div>

  <!-- ── CHAT BOX ── -->
  <div class="pxl-section chat-section" *ngIf="isInRoom && chatEnabled">
    <div class="pxl-section-label chat-label">💬 Chat</div>
    <div class="chat-messages" #chatMessagesEl>
      <ng-container *ngIf="chatMessages$ | async as msgs">
        <div *ngFor="let msg of msgs; trackBy: trackById"
             class="chat-msg"
             [class.chat-msg--self]="msg.username === localUsername"
             [class.chat-msg--system]="msg.username === 'System'"
             [class.chat-msg--highlight]="msg.id === highlightedMsgId">
          <span class="chat-avatar" [style.background]="msg.color">{{ msg.avatar }}</span>
          <div class="chat-bubble">
            <span class="chat-sender">{{ msg.username }}</span>
            <span class="chat-text">{{ msg.text }}</span>
            <span class="chat-time">{{ msg.timestamp | date:'HH:mm' }}</span>
          </div>
        </div>
        <div class="chat-empty" *ngIf="msgs.length === 0">No messages yet</div>
      </ng-container>
    </div>
    <div class="emoji-picker" *ngIf="showEmojiPicker">
      <button class="emoji-btn" *ngFor="let e of EMOJIS" (click)="insertEmoji(e)">{{ e }}</button>
    </div>
    <div class="chat-input-row">
      <button class="pxl-btn chat-emoji-toggle" (click)="toggleEmojiPicker()" [disabled]="chatMutedForMe">😊</button>
      <input class="pxl-input chat-input" [(ngModel)]="chatInput" (keydown.enter)="sendChat()"
             [placeholder]="chatMutedForMe ? 'Chat disabled by admin' : 'Message…'"
             [disabled]="chatMutedForMe" maxlength="500">
      <button class="pxl-btn pxl-btn-primary chat-send-btn" (click)="sendChat()" [disabled]="!chatInput.trim() || chatMutedForMe">↑</button>
    </div>
    <div class="chat-muted-note" *ngIf="chatMutedForMe">Chat is disabled for your user by admin.</div>
  </div>

  <!-- ── IMPORT IMAGE / VIDEO ── -->
  <div class="pxl-section import-section">
    <div class="pxl-section-label">↑ Import Image / Video</div>

    <div class="import-tab-row">
      <button class="import-tab-btn" [class.active]="importTab === 'image'" (click)="importTab = 'image'">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
        Image
      </button>
      <button class="import-tab-btn" [class.active]="importTab === 'video'" (click)="importTab = 'video'">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
        Video
      </button>
    </div>

    <!-- IMAGE tab -->
    <ng-container *ngIf="importTab === 'image'">
      <label class="import-drop-zone" for="img-upload-sidebar">
        <span class="import-icon"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></span>
        <span class="import-text">Drop or click to upload</span>
        <span class="import-sub">Positions on canvas before committing</span>
        <input type="file" id="img-upload-sidebar" accept="image/*" (change)="importImageTriggered($event)">
      </label>
    </ng-container>

    <!-- VIDEO tab -->
    <ng-container *ngIf="importTab === 'video'">
      <ng-container *ngIf="!videoFile">
        <label class="import-drop-zone" for="vid-upload-sidebar">
          <span class="import-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
          </span>
          <span class="import-text">Drop or click to upload video</span>
          <span class="import-sub">MP4 · WebM · MOV · AVI</span>
          <input type="file" id="vid-upload-sidebar" accept="video/*" (change)="onVideoFileSelected($event)">
        </label>
      </ng-container>

      <ng-container *ngIf="videoFile">
        <div class="video-file-row">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
          <span class="video-file-name">{{ videoFile.name }}</span>
          <button class="video-clear-btn" (click)="clearVideoFile()" title="Remove">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="video-meta-row" *ngIf="videoDuration > 0">
          <span class="video-meta-chip">{{ formatDuration(videoDuration) }}</span>
          <span class="video-meta-chip" *ngIf="videoNativeWidth > 0">{{ videoNativeWidth }}×{{ videoNativeHeight }}</span>
          <span class="video-meta-chip" *ngIf="estimatedFrameCount > 0">≈ {{ estimatedFrameCount }}f</span>
        </div>
        <button class="pxl-btn pxl-btn-secondary vid-open-modal-btn" (click)="openVideoModal()">
          ⚙ Preview &amp; Settings
        </button>
        <button class="pxl-btn pxl-btn-primary vid-extract-btn" [disabled]="videoImporting" (click)="processVideoImport()">
          <span *ngIf="!videoImporting">Extract Pixel Frames</span>
          <span *ngIf="videoImporting" class="loading-row"><span class="pxl-spinner"></span>Processing… {{ videoImportProgress }}%</span>
        </button>
        <div class="vid-progress-wrap" *ngIf="videoImporting">
          <div class="vid-progress-bar" [style.width.%]="videoImportProgress"></div>
        </div>
        <div class="vid-status" *ngIf="videoImportStatus"
             [class.success]="videoImportStatusTone === 'success'"
             [class.error]="videoImportStatusTone === 'error'">{{ videoImportStatus }}</div>
        <label class="pxl-btn pxl-btn-secondary pxl-btn-sm vid-change-btn" for="vid-reupload">
          Change video<input type="file" id="vid-reupload" accept="video/*" (change)="onVideoFileSelected($event)" style="display:none">
        </label>
      </ng-container>
    </ng-container>
  </div>


</div>

<!-- ══════════════════════════════════════════════════════════════════════════ -->
<!-- ── VIDEO IMPORT MODAL ──                                                  -->
<!-- ══════════════════════════════════════════════════════════════════════════ -->
<div class="video-modal-backdrop" *ngIf="showVideoModal" (click)="onVideoModalBackdropClick($event)">
  <div class="video-modal">

    <!-- Header -->
    <div class="video-modal-header">
      <span class="video-modal-title">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-2px;margin-right:5px"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
        Import Video
      </span>
      <span class="video-modal-filename" *ngIf="videoFile">{{ videoFile.name }}</span>
      <button class="video-modal-close" (click)="closeVideoModal()">✕</button>
    </div>

    <!-- Two-column body -->
    <div class="video-modal-body">

      <!-- LEFT: video player + crop overlay + trim controls -->
      <div class="video-modal-left">

        <!-- Video player container (position:relative so crop overlay can sit on top) -->
        <div class="video-player-container">
          <!-- THE VIDEO - src set via JS, NOT Angular [src] binding to avoid reload loops -->
          <video #videoModalPlayerEl
                 class="video-modal-player"
                 [muted]="importVideoMuted"
                 playsinline
                 preload="auto"
                 (loadedmetadata)="onImportVideoLoadedMetadata(videoModalPlayerEl)"
                 (timeupdate)="onImportVideoTimeUpdate(videoModalPlayerEl)"
                 (play)="onImportVideoPlayState(true)"
                 (pause)="onImportVideoPlayState(false)">
          </video>

          <!-- Crop overlay - shown whenever video dimensions are known (all modes) -->
          <div class="crop-overlay-wrap"
               #cropOverlayEl
               *ngIf="videoNativeWidth > 0 && videoRenderInfo as ri"
               [style.left.px]="ri.left"
               [style.top.px]="ri.top"
               [style.width.px]="ri.w"
               [style.height.px]="ri.h">

            <!-- Dark masks around the selected region -->
            <div class="crop-mask crop-mask-top"    [style.height.%]="cropTop * 100"></div>
            <div class="crop-mask crop-mask-bottom" [style.height.%]="(1 - cropBottom) * 100"></div>
            <div class="crop-mask crop-mask-left"
                 [style.top.%]="cropTop * 100"
                 [style.height.%]="(cropBottom - cropTop) * 100"
                 [style.width.%]="cropLeft * 100"></div>
            <div class="crop-mask crop-mask-right"
                 [style.top.%]="cropTop * 100"
                 [style.height.%]="(cropBottom - cropTop) * 100"
                 [style.width.%]="(1 - cropRight) * 100"></div>

            <!-- Crop box (border + handles) -->
            <div class="crop-box"
                 [style.left.%]="cropLeft * 100"
                 [style.top.%]="cropTop * 100"
                 [style.width.%]="(cropRight - cropLeft) * 100"
                 [style.height.%]="(cropBottom - cropTop) * 100">

              <!-- Move area (click-drag inside crop box to reposition) -->
              <div class="crop-move-area" (mousedown)="onCropHandleDown($event, 'move')"></div>

              <!-- Rule-of-thirds grid lines -->
              <div class="crop-grid-line crop-grid-v" style="left:33.33%"></div>
              <div class="crop-grid-line crop-grid-v" style="left:66.66%"></div>
              <div class="crop-grid-line crop-grid-h" style="top:33.33%"></div>
              <div class="crop-grid-line crop-grid-h" style="top:66.66%"></div>

              <!-- Corner handles -->
              <div class="crop-handle crop-h-tl" (mousedown)="onCropHandleDown($event, 'tl')"></div>
              <div class="crop-handle crop-h-tr" (mousedown)="onCropHandleDown($event, 'tr')"></div>
              <div class="crop-handle crop-h-bl" (mousedown)="onCropHandleDown($event, 'bl')"></div>
              <div class="crop-handle crop-h-br" (mousedown)="onCropHandleDown($event, 'br')"></div>
              <!-- Edge handles -->
              <div class="crop-handle crop-h-t"  (mousedown)="onCropHandleDown($event, 't')"></div>
              <div class="crop-handle crop-h-b"  (mousedown)="onCropHandleDown($event, 'b')"></div>
              <div class="crop-handle crop-h-l"  (mousedown)="onCropHandleDown($event, 'l')"></div>
              <div class="crop-handle crop-h-r"  (mousedown)="onCropHandleDown($event, 'r')"></div>
              <!-- Crop size readout - inside crop-box so it stays visible at any position -->
              <div class="crop-size-readout">
                {{ ((cropRight - cropLeft) * videoNativeWidth) | number:'1.0-0' }}×{{ ((cropBottom - cropTop) * videoNativeHeight) | number:'1.0-0' }}
              </div>
            </div>
            <!-- /crop-box -->

          </div>
          <!-- /crop-overlay-wrap -->
        </div>
        <!-- /video-player-container -->

        <!-- ── Video player controls - now BELOW video, no longer overlapping crop overlay ── -->
        <div class="video-import-controls" *ngIf="videoDuration > 0">
          <div class="video-preview-time-row">
            <span class="video-preview-time">{{ formatDuration(importVideoCurrentTime) }}</span>
            <input type="range" class="video-preview-slider"
                   min="0" [max]="videoDuration" step="0.01"
                   [value]="importVideoCurrentTime"
                   (input)="seekImportVideo($any($event.target).value, videoModalPlayerEl)">
            <span class="video-preview-time">{{ formatDuration(videoDuration) }}</span>
          </div>
          <div class="video-preview-control-row">
            <div class="video-preview-control-group">
              <!-- Play/Pause -->
              <button class="video-control-btn" (click)="toggleImportPlayback(videoModalPlayerEl)" title="Play / Pause">
                <svg *ngIf="!importVideoIsPlaying" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <polygon points="8 5 19 12 8 19 8 5"></polygon>
                </svg>
                <svg *ngIf="importVideoIsPlaying" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
                  <line x1="9" y1="5" x2="9" y2="19"></line>
                  <line x1="15" y1="5" x2="15" y2="19"></line>
                </svg>
              </button>
              <!-- Mute toggle -->
              <button *ngIf="importVideoHasAudio" class="video-control-btn" (click)="toggleImportMuted(videoModalPlayerEl)" title="Mute / Unmute">
                <svg *ngIf="!importVideoMuted" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                  <path d="M15 9a5 5 0 0 1 0 6"></path>
                  <path d="M18.5 6.5a9 9 0 0 1 0 11"></path>
                </svg>
                <svg *ngIf="importVideoMuted" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                  <line x1="23" y1="9" x2="17" y2="15"></line>
                  <line x1="17" y1="9" x2="23" y2="15"></line>
                </svg>
              </button>
              <!-- Volume slider -->
              <div *ngIf="importVideoHasAudio" class="video-preview-volume-wrap">
                <input type="range" class="video-preview-volume" min="0" max="1" step="0.01"
                       [value]="importVideoVolume"
                       (input)="setImportVolume($any($event.target).value, videoModalPlayerEl)">
              </div>
              <div *ngIf="!importVideoHasAudio" class="video-preview-note">No audio</div>
            </div>
            <div class="video-preview-control-group video-preview-control-group-right">
              <!-- Fullscreen -->
              <button class="video-control-btn" (click)="toggleImportFullscreen(videoModalPlayerEl)" title="Fullscreen">
                <svg *ngIf="!importVideoIsFullscreen" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <polyline points="9 21 3 21 3 15"></polyline>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
                <svg *ngIf="importVideoIsFullscreen" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="4 14 10 14 10 20"></polyline>
                  <polyline points="20 10 14 10 14 4"></polyline>
                  <line x1="14" y1="10" x2="21" y2="3"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Trim (timeline crop) controls -->
        <div class="crop-section" *ngIf="videoDuration > 0">
          <div class="crop-section-title">Trim Timeline</div>
          <div class="crop-row">
            <span class="crop-label">Start</span>
            <input type="range" class="vid-range crop-range"
                   [(ngModel)]="videoCropStart"
                   [min]="0" [max]="videoCropEnd > 0 ? videoCropEnd : videoDuration" step="0.1"
                   (input)="schedulePreviewRefresh()">
            <span class="crop-val">{{ formatDuration(videoCropStart) }}</span>
          </div>
          <div class="crop-row">
            <span class="crop-label">End</span>
            <input type="range" class="vid-range crop-range"
                   [(ngModel)]="videoCropEnd" [min]="videoCropStart" [max]="videoDuration" step="0.1"
                   (input)="schedulePreviewRefresh()">
            <span class="crop-val">{{ formatDuration(videoCropEnd) }}</span>
          </div>
          <div class="crop-duration-chip" *ngIf="videoCropEnd > videoCropStart">
            {{ formatDuration(videoCropEnd - videoCropStart) }} selected
          </div>
        </div>

      </div>
      <!-- /video-modal-left -->

      <!-- RIGHT: settings -->
      <div class="video-modal-right">
        <div class="video-modal-settings-title">Settings</div>

        <div class="vid-settings">

          <!-- Pixel Size -->
          <div class="vid-row">
            <label class="vid-label">Pixel Size</label>
            <div class="vid-slider-wrap">
              <input type="range" [(ngModel)]="videoPixelSize" min="1" max="16" step="1"
                     class="vid-range" (input)="schedulePreviewRefresh()">
              <span class="vid-val">{{ videoPixelSize }}px</span>
            </div>
          </div>

          <!-- FPS -->
          <div class="vid-row">
            <label class="vid-label">Extract FPS</label>
            <div class="vid-slider-wrap vid-fps-wrap">
              <input type="range" [(ngModel)]="videoFps" min="1"
                     [max]="videoNativeFps > 0 ? videoNativeFps : 120" step="1"
                     class="vid-range"
                     (mousedown)="videoFpsDragging = true"
                     (touchstart)="videoFpsDragging = true"
                     (input)="onFpsSliderInput($event)">
              <!-- Drag tooltip - shows value while dragging so no static text overlaps -->
              <div class="vid-fps-tooltip" *ngIf="videoFpsDragging" [style.left.%]="videoFpsTooltipPos">
                {{ videoFps }}fps
              </div>
            </div>
            <!-- Detecting spinner -->
            <span *ngIf="videoFpsDetecting" class="vid-fps-detecting">
              <span class="pxl-spinner" style="width:8px;height:8px;border-width:1.5px;display:inline-block"></span>
            </span>
            <!-- When native FPS known: show current/max together in the badge -->
            <span *ngIf="videoNativeFps > 0 && !videoFpsDetecting" class="vid-fps-badge"
                  [title]="'Extracting ' + videoFps + ' fps · Video native: ' + videoNativeFps + ' fps'">
              {{ videoFps }}<span class="vid-fps-badge-max">/{{ videoNativeFps }}fps</span>
            </span>
            <!-- When native FPS not yet known: plain value -->
            <span *ngIf="videoNativeFps === 0 && !videoFpsDetecting" class="vid-val">{{ videoFps }}</span>
          </div>

          <!-- Max Frames -->
          <div class="vid-row">
            <label class="vid-label">Max Frames</label>
            <div class="vid-maxframes-wrap">
              <input type="number" [(ngModel)]="videoMaxFrames" min="0" step="1"
                     class="pxl-input vid-maxframes-input"
                     [placeholder]="'All (' + estimatedFrameCount + ')'">
              <button class="pxl-btn pxl-btn-secondary pxl-btn-sm vid-maxframes-all-btn"
                      (click)="videoMaxFrames = 0" [class.active]="videoMaxFrames === 0"
                      title="Extract all frames">All</button>
            </div>
          </div>

          <!-- Dithering -->
          <div class="vid-row">
            <label class="vid-label">Dithering</label>
            <label class="pxl-toggle" style="margin-left:auto">
              <input type="checkbox" [(ngModel)]="videoDithering" (ngModelChange)="schedulePreviewRefresh()">
              <span class="pxl-toggle-track"><span class="pxl-toggle-thumb"></span></span>
            </label>
          </div>
          <div class="vid-row" *ngIf="videoDithering">
            <label class="vid-label">Dither Str.</label>
            <div class="vid-slider-wrap">
              <input type="range" [(ngModel)]="videoDitherStrength" min="1" max="4" step="1"
                     class="vid-range" (input)="schedulePreviewRefresh()">
              <span class="vid-val">{{ videoDitherStrength }}</span>
            </div>
          </div>

          <!-- Import Mode -->
          <div class="vid-row">
            <label class="vid-label">Mode</label>
            <select class="pxl-input vid-select" [(ngModel)]="videoImportMode">
              <option value="append">Append to timeline</option>
              <option value="replace">Replace all frames</option>
            </select>
          </div>

        </div>
        <!-- /vid-settings -->

        <!-- ── CROP & FRAME ── all presets (behavior + ratio) in one unified area -->
        <div class="vid-section-divider">Crop &amp; Frame
          <span *ngIf="cropAspectLock" class="crop-lock-badge" title="Aspect ratio locked">🔒</span>
        </div>
        <!-- Single unified row: behavior presets · separator · ratio presets -->
        <div class="ratio-presets-row">
          <!-- Behavior presets -->
          <button class="ratio-btn" [class.active]="videoCropPreset === 'fill'"
                  (click)="applyCropPreset('fill')" title="Center-crop to fill frame - no black bars">Fill</button>
          <button class="ratio-btn" [class.active]="videoCropPreset === 'fit'"
                  (click)="applyCropPreset('fit')" title="Letterbox - full video visible, black bars">Fit</button>
          <button class="ratio-btn" [class.active]="videoCropPreset === 'stretch'"
                  (click)="applyCropPreset('stretch')" title="Stretch to fill - may distort">Stretch</button>
          <button class="ratio-btn" [class.active]="videoCropPreset === 'free'"
                  (click)="applyCropPreset('free')" title="Free crop - drag handles freely">Free</button>
          <!-- Visual separator -->
          <span class="ratio-sep"></span>
          <!-- Ratio presets (lock aspect on drag) -->
          <button class="ratio-btn" [class.active]="activeRatioPreset === '1:1'"  (click)="setCropRatio(1,1)"   title="1:1 Square">1:1</button>
          <button class="ratio-btn" [class.active]="activeRatioPreset === '4:3'"  (click)="setCropRatio(4,3)"   title="4:3">4:3</button>
          <button class="ratio-btn" [class.active]="activeRatioPreset === '16:9'" (click)="setCropRatio(16,9)"  title="16:9 Widescreen">16:9</button>
          <button class="ratio-btn" [class.active]="activeRatioPreset === '9:16'" (click)="setCropRatio(9,16)"  title="9:16 Portrait">9:16</button>
          <button class="ratio-btn" [class.active]="activeRatioPreset === '3:2'"  (click)="setCropRatio(3,2)"   title="3:2">3:2</button>
          <button class="ratio-btn" [class.active]="activeRatioPreset === '2:3'"  (click)="setCropRatio(2,3)"   title="2:3">2:3</button>
          <button class="ratio-btn ratio-btn-reset" (click)="resetCrop()" title="Full frame">Full</button>
        </div>
        <div class="crop-coords-info" *ngIf="videoNativeWidth > 0">
          <span>L {{ (cropLeft * 100) | number:'1.0-0' }}%</span>
          <span>T {{ (cropTop * 100) | number:'1.0-0' }}%</span>
          <span>R {{ (cropRight * 100) | number:'1.0-0' }}%</span>
          <span>B {{ (cropBottom * 100) | number:'1.0-0' }}%</span>
        </div>

        <!-- Estimated frame count -->
        <div class="vid-estimate" *ngIf="estimatedFrameCount > 0" style="margin-top:8px">
          ≈ {{ estimatedFrameCount }} frames will be extracted
        </div>

      </div>
      <!-- /video-modal-right -->

    </div>
    <!-- /video-modal-body -->

    <!-- Frame Preview Strip -->
    <div class="video-preview-strip">
      <div class="preview-strip-header">
        <span class="preview-strip-title">
          Frame Preview
          <span *ngIf="videoPreviewFrames.length > 0" class="preview-frame-count">({{ videoPreviewFrames.length }})</span>
        </span>
        <div class="preview-strip-actions">
          <!-- Play / Pause animation button -->
          <button class="pxl-btn pxl-btn-secondary pxl-btn-sm preview-play-btn"
                  *ngIf="videoPreviewFrames.length > 1"
                  (click)="togglePreviewPlay()"
                  [title]="previewIsPlaying ? 'Pause preview' : 'Play preview'">
            <svg *ngIf="!previewIsPlaying" width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <polygon points="8 5 19 12 8 19 8 5"></polygon>
            </svg>
            <svg *ngIf="previewIsPlaying" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="9" y1="5" x2="9" y2="19"></line>
              <line x1="15" y1="5" x2="15" y2="19"></line>
            </svg>
            {{ previewIsPlaying ? 'Pause' : 'Play' }}
          </button>
          <!-- Refresh button -->
          <button class="preview-refresh-btn pxl-btn pxl-btn-secondary pxl-btn-sm"
                  (click)="generatePreviewFrames()" [disabled]="previewGenerating || !videoFile">
            <ng-container *ngIf="!previewGenerating">↺ Refresh</ng-container>
            <span *ngIf="previewGenerating" class="pxl-spinner" style="width:10px;height:10px;border-width:1.5px;display:inline-block"></span>
          </button>
        </div>
      </div>
      <!-- Large pixel-art preview of the currently selected frame -->
      <div class="preview-current-display" *ngIf="videoPreviewFrames.length > 0 && selectedPreviewIndex >= 0">
        <img [src]="videoPreviewFrames[selectedPreviewIndex]" class="preview-current-img" alt="Current frame">
        <span class="preview-current-label">{{ previewTimestamps[selectedPreviewIndex] }} · Frame {{ selectedPreviewIndex + 1 }}/{{ videoPreviewFrames.length }}</span>
      </div>

      <div class="preview-strip-frames" #previewStripEl>
        <ng-container *ngIf="videoPreviewFrames.length > 0">
          <div class="preview-frame-wrap"
               #previewFrameRef
               *ngFor="let frame of videoPreviewFrames; let i = index"
               [class.active]="i === selectedPreviewIndex"
               (click)="seekVideoPreviewFrame(i)"
               title="Jump to {{ previewTimestamps[i] }}">
            <img [src]="frame" class="preview-frame-img">
            <span class="preview-frame-time">{{ previewTimestamps[i] }}</span>
          </div>
        </ng-container>
        <div class="preview-empty" *ngIf="!videoPreviewFrames.length && !previewGenerating">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="opacity:0.4"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          <span>Click Refresh to generate preview</span>
        </div>
        <div class="preview-generating" *ngIf="previewGenerating">
          <span class="pxl-spinner" style="width:14px;height:14px;border-width:2px"></span>
          <span>Generating {{ estimatedFrameCount > 0 ? estimatedFrameCount + ' frames' : 'preview' }}…</span>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="video-modal-footer">
      <div class="vid-progress-wrap" *ngIf="videoImporting" style="margin-bottom:6px">
        <div class="vid-progress-bar" [style.width.%]="videoImportProgress"></div>
      </div>
      <div class="vid-progress-label" *ngIf="videoImporting">Extracting… {{ videoImportProgress }}%</div>
      <div class="vid-status" *ngIf="videoImportStatus"
           [class.success]="videoImportStatusTone === 'success'"
           [class.error]="videoImportStatusTone === 'error'"
           style="margin-bottom:8px">{{ videoImportStatus }}</div>
      <div class="video-modal-footer-actions">
        <label class="pxl-btn pxl-btn-secondary pxl-btn-sm" for="vid-modal-reupload" style="cursor:pointer">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
          Change
          <input type="file" id="vid-modal-reupload" accept="video/*" (change)="onVideoFileSelected($event)" style="display:none">
        </label>
        <button class="pxl-btn pxl-btn-secondary" (click)="closeVideoModal()">Cancel</button>
        <button class="pxl-btn pxl-btn-primary vid-modal-extract-btn"
                [disabled]="videoImporting || !videoFile" (click)="processVideoImport()">
          <span *ngIf="!videoImporting">Extract Pixel Frames</span>
          <span *ngIf="videoImporting" class="loading-row"><span class="pxl-spinner"></span>Processing…</span>
        </button>
      </div>
    </div>

  </div>
</div>

